import React, { ReactNode, useState } from "react";
import { FieldArray, FormikProps } from "formik";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";
import ATMTextField from "src/components/UI/atoms/formFields/ATMTextField/ATMTextField";
import ATMTextArea from "src/components/UI/atoms/formFields/ATMTextArea/ATMTextArea";
import ATMSelect from "src/components/UI/atoms/formFields/ATMSelect/ATMSelect";
import { GrFormAdd } from "react-icons/gr";
import { ProductFormValues } from "src/models/Product.model";
import useModes from "src/hooks/useMode";
import { RiDeleteBin5Line } from "react-icons/ri";
import { getInputHeight } from "src/utils/formUtils/getInputHeight";
import ATMMultiSelect from "src/components/UI/atoms/formFields/ATMMultiSelect/ATMMultiSelect";
import { useNavigate } from "react-router-dom";
import useSubjects from "src/hooks/useSubject";
import useFaculty from "src/hooks/useFaculty";
import useCourse from "src/hooks/useCourse";
import useLevels from "src/hooks/useLevels";
import useCategory from "src/hooks/useCategory";
import useGroup from "src/hooks/useGroup";
import useModule from "src/hooks/useModule";
import useSyllabus from "src/hooks/useSyllabus";
import useProduct from "src/hooks/useProduct";
import { handleValidNumber } from "src/utils/validations/numberInput";
import {
  handleValidDecimalNumber,
  handleValidNumber2,
} from "src/utils/formUtils/ValidNumber";
import ATMFilePickerWrapper from "src/components/UI/atoms/formFields/ATMFileUploader/ATMFileUploaderWrapper";
import { AiOutlinePlus } from "react-icons/ai";
import { MdDeleteOutline } from "react-icons/md";
import ATMTextFieldWithSwitch from "src/components/UI/atoms/formFields/ATMTextFieldWithSwitch/ATMTextFieldWithSwitch";
import useInstitutes from "src/hooks/useInstitute";
import ATMDatePicker from "src/components/UI/atoms/formFields/ATMDatePicker/ATMDatePicker";
import useBatchTypes from "src/hooks/useBatchType";

type Props = {
  formikProps: FormikProps<ProductFormValues>;
  formType: "ADD" | "EDIT";
};
const FormSectionHeading = ({ children }: { children: ReactNode }) => {
  return (
    <div className="border-l-[3px] border-primary-main px-3 py-1 text-theme font font-medium text-[18px] sticky top-0 bg-white">
      {children}
    </div>
  );
};

const ProductManagement = ({ formikProps, formType }: Props) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState({
    product: true,
    payment: false,
    other: false,
  });
  const toggle = (key: keyof typeof open) => {
    setOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const { values, setFieldValue, isSubmitting, handleBlur } = formikProps;
  const { subjects, isDataLoading } = useSubjects();
  const { faculty, isDataLoading: isFacultyLoading } = useFaculty();
  const { course, isDataLoading: isCourseLoading } = useCourse();
  const { levels, isDataLoading: isLevelLoading } = useLevels();
  const { category, isDataLoading: isCategoryLoading } = useCategory();
  const { group, isDataLoading: isGroupLoading } = useGroup();
  const { modes, isDataLoading: isModeLoading } = useModes();
  const { module, isDataLoading: isModuleLoading } = useModule();
  const { syllabus, isDataLoading: isSyllabusLoading } = useSyllabus();
  const { product, isDataLoading: isRelevantProductLoading } = useProduct();
  const { institutes, isDataLoading: isInstituteLoading } = useInstitutes();
  const { batchTypes, isDataLoading: isBatchTypeLoading } = useBatchTypes();
  console.log(values.relevantProducts,values.studentAlsoPurchased)
  const getBatchTypeOptions = batchTypes?.map((batch) => {
    return { value: batch._id, label: batch.batchTypeName };
  });
  const getRelevantProductsOptions = product?.map((product) => {
    return { value: product.code, label: product.productName };
  });
  const getSyllabusOptions = syllabus?.map((syllabus) => {
    return { value: syllabus._id, label: syllabus.syllabusName };
  });
  const getModuleOptions = module?.map((module) => {
    return { value: module._id, label: module.moduleName };
  });
  const getModesOptions = modes?.map((modes) => {
    return { value: modes._id, label: modes.modeName };
  });
  const getCategoryOptions = category?.map((category) => {
    return { value: category._id, label: category.categoryName };
  });
  const getGroupOptions = group?.map((group) => {
    return { value: group._id, label: group.groupName };
  });
  const getLevelsOptions = levels?.map((levels) => {
    return { value: levels._id, label: levels.levelName };
  });
  const getCourseOptions = course?.map((course) => {
    return { value: course._id, label: course.courseName };
  });
  const getSubjectOptions = subjects?.map((subject) => {
    return { value: subject._id, label: subject.subjectName };
  });
  const getFacultyOptions = faculty?.map((faculty) => {
    return { value: faculty._id, label: faculty.facultyName };
  });
  const getInstituteOptions = institutes?.map((institute) => {
    return { value: institute._id, label: institute.instituteName };
  });
  const paymentGatewayOptions = [
    { label: "Razorpay", value: "Razorpay" },
    { label: "Paytm", value: "Paytm" },
  ];
  const selectOptions = [
    { label: "Yes", value: "YES" },
    { label: "No", value: "NO" },
  ];
  const percentageOptions = [
    { label: "Flat", value: "FLAT" },
    { label: "Percent", value: "PERCENT" },
  ];
  return (
    <div className="p-4 h-full flex flex-col gap-6">
      <div className="flex justify-between items-center">
        <div className="text-xl font-medium">
          {formType === "ADD" ? "Add product" : "Update Product"}
        </div>
        <button
          type="button"
          className="hover:bg-hover"
          onClick={() => navigate("/products")}
        >
          Cancel
        </button>
      </div>
      <div className="flex-1 overflow-auto flex flex-col gap-3">
        <h1
          onClick={() => toggle("product")}
          className="w-full cursor-pointer flex justify-between items-center p-3 bg-gray-100"
        >
          Product Details
        </h1>
        {open.product && (
          <>
            <FormSectionHeading>Basic Details</FormSectionHeading>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 p-2">
              <div>
                <ATMTextField
                  placeholder="Product Name"
                  name="productName"
                  value={values.productName}
                  required
                  onChange={(e) => {
                    setFieldValue("productName", e.target.value);
                  }}
                  label="Product Name"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder=" Code"
                  name="code"
                  value={values.code}
                  required
                  onChange={(e) => {
                    setFieldValue("code", e.target.value);
                  }}
                  label=" Code"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder="Varient Code"
                  name="varientCode"
                  value={values.varientCode}
                  required
                  onChange={(e) => {
                    setFieldValue("varientCode", e.target.value);
                  }}
                  label="Varient Code"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder="Nick Name"
                  name="nickname"
                  value={values.nickname}
                  required
                  onChange={(e) => {
                    setFieldValue("nickname", e.target.value);
                  }}
                  label="Nick Name"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMSelect
                  placeholder="select course"
                  name="courseName"
                  isLoading={isCourseLoading}
                  options={getCourseOptions}
                  value={values.courseName}
                  required
                  onChange={(e) => {
                    setFieldValue("courseName", e);
                  }}
                  label=" Course Name"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMSelect
                  placeholder="select level"
                  name="levelName"
                  isLoading={isLevelLoading}
                  options={getLevelsOptions}
                  value={values.levelName}
                  required
                  onChange={(e) => {
                    setFieldValue("levelName", e);
                  }}
                  label="Level Name"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMSelect
                  placeholder="select Batch Type"
                  name="batchType"
                  isLoading={isBatchTypeLoading}
                  options={getBatchTypeOptions}
                  value={values.batchType}
                  required
                  onChange={(e) => {
                    setFieldValue("batchType", e);
                  }}
                  label="Batch Type"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMMultiSelect
                  isLoading={isDataLoading}
                  name={`subject`}
                  required
                  showAddButton
                  // placeholder="select subject"
                  label="Subject"
                  options={getSubjectOptions}
                  onChange={(newValue: any) => {
                    setFieldValue(`subject`, newValue);
                  }}
                  value={values.subject}
                />
              </div>

              <div>
                <ATMMultiSelect
                  isLoading={isFacultyLoading}
                  name={`faculty`}
                  required
                  showAddButton
                  placeholder="select faculty"
                  label="Faculty"
                  options={getFacultyOptions}
                  onChange={(newValue: any) => {
                    setFieldValue(`faculty`, newValue);
                  }}
                  value={values.faculty}
                />
              </div>
              <div>
                <ATMSelect
                  placeholder="select module"
                  name="moduleName"
                  isLoading={isModuleLoading}
                  options={getModuleOptions}
                  value={values.moduleName}
                  required
                  onChange={(e) => {
                    setFieldValue("moduleName", e);
                  }}
                  label="Module Name"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMSelect
                  placeholder="select group"
                  name="groupName"
                  isLoading={isGroupLoading}
                  options={getGroupOptions}
                  value={values.groupName}
                  required
                  onChange={(e) => {
                    setFieldValue("groupName", e);
                  }}
                  label="Group Name"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMSelect
                  placeholder="select syllabus"
                  name="syllabusName"
                  isLoading={isSyllabusLoading}
                  options={getSyllabusOptions}
                  value={values.syllabusName}
                  required
                  onChange={(e) => {
                    setFieldValue("syllabusName", e);
                  }}
                  label="Syllabus Name"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMMultiSelect
                  isLoading={isInstituteLoading}
                  name={`instituteName`}
                  required
                  showAddButton
                  // placeholder="select subject"
                  label="select institute"
                  options={getInstituteOptions}
                  onChange={(newValue: any) => {
                    setFieldValue(`instituteName`, newValue);
                  }}
                  value={values.instituteName}
                />
              </div>
              <div>
                <ATMSelect
                  placeholder="select mode"
                  name="modeName"
                  isLoading={isModeLoading}
                  options={getModesOptions}
                  value={values.modeName}
                  required
                  onChange={(e) => {
                    setFieldValue("modeName", e);
                  }}
                  label="Mode Name"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMSelect
                  placeholder="select category"
                  name="categoryName"
                  isLoading={isCategoryLoading}
                  options={getCategoryOptions}
                  value={values.categoryName}
                  required
                  onChange={(e) => {
                    setFieldValue("categoryName", e);
                  }}
                  label="Category Name"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder="rank"
                  name="rank"
                  value={values.rank}
                  required
                  onChange={(e) => {
                    handleValidDecimalNumber(e) &&
                      setFieldValue("rank", e.target.value);
                  }}
                  label="Rank"
                  onBlur={handleBlur}
                />
              </div>
            </div>
            <div>
              <ATMTextArea
                name="importantNotice"
                value={values.importantNotice}
                onChange={(e) => {
                  setFieldValue("importantNotice", e);
                }}
                label="Important Notice"
              />
            </div>
            <div>
              <ATMTextArea
                name="productNotes"
                value={values.productNotes}
                onChange={(e) => {
                  setFieldValue("productNotes", e);
                }}
                label="Product Notes"
              />
            </div>
          </>
        )}
        <h1
          onClick={() => toggle("payment")}
          className="w-full cursor-pointer flex justify-between items-center p-3 bg-gray-100"
        >
          Payment Details
        </h1>
        {open.payment && (
          <>
            <FormSectionHeading>General Details</FormSectionHeading>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 p-2">
              <div>
                <ATMSelect
                  name="isCombo"
                  options={selectOptions}
                  value={values.isCombo}
                  required
                  onChange={(e) => {
                    setFieldValue("isCombo", e);
                  }}
                  label="Is Combo"
                  onBlur={handleBlur}
                />
              </div>

              <div>
                <ATMSelect
                  name="isAvailable"
                  options={selectOptions}
                  value={values.isAvailable}
                  required
                  onChange={(e) => {
                    setFieldValue("isAvailable", e);
                  }}
                  label="Is Available"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMSelect
                  name="inOffer"
                  options={selectOptions}
                  value={values.inOffer}
                  required
                  onChange={(e) => {
                    setFieldValue("inOffer", e);
                  }}
                  label="In Offer"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder=""
                  name="productMrp"
                  value={values.productMrp}
                  required
                  onChange={(e) => {
                    handleValidDecimalNumber(e) &&
                      setFieldValue("productMrp", e.target.value);
                  }}
                  label="Product MRP"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextFieldWithSwitch
                  options={percentageOptions}
                  name="maxDiscountType"
                  numberName="maxDiscount"
                  required
                  label="Max Discount"
                  valueSelect={values.maxDiscountType}
                  valueNumber={values.maxDiscount}
                  onSelectChange={(selected) => {
                    setFieldValue("maxDiscountType", selected);
                  }}
                  onNumberChange={(e) => {
                    handleValidDecimalNumber(e) &&
                      setFieldValue("maxDiscount", e.target.value);
                  }}
                  onNumberBlur={handleBlur}
                />
              </div>

              <div>
                <ATMTextFieldWithSwitch
                  options={percentageOptions}
                  name="referralToPercentageType"
                  numberName="referralToPercentage"
                  required
                  label="Referral To Percentage"
                  valueSelect={values.referralToPercentageType}
                  valueNumber={values.referralToPercentage}
                  onSelectChange={(selected) => {
                    setFieldValue("referralToPercentageType", selected);
                  }}
                  onNumberChange={(e) => {
                    handleValidDecimalNumber(e) &&
                      setFieldValue("referralToPercentage", e.target.value);
                  }}
                  onNumberBlur={handleBlur}
                />
              </div>

              {/* referral by percentage */}
              <div>
                <ATMTextFieldWithSwitch
                  options={percentageOptions}
                  name="referralByPercentageType"
                  numberName="referralByPercentage"
                  required
                  label="Referral By Percentage"
                  valueSelect={values.referralByPercentageType}
                  valueNumber={values.referralByPercentage}
                  onSelectChange={(selected) => {
                    setFieldValue("referralByPercentageType", selected);
                  }}
                  onNumberChange={(e) => {
                    handleValidDecimalNumber(e) &&
                      setFieldValue("referralByPercentage", e.target.value);
                  }}
                  onNumberBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextFieldWithSwitch
                  options={percentageOptions}
                  name="walletDiscountType"
                  numberName="walletDiscount"
                  required
                  label="Wallet Discount"
                  valueSelect={values.walletDiscountType}
                  valueNumber={values.walletDiscount}
                  onSelectChange={(selected) => {
                    setFieldValue("walletDiscountType", selected);
                  }}
                  onNumberChange={(e) => {
                    handleValidDecimalNumber(e) &&
                      setFieldValue("walletDiscount", e.target.value);
                  }}
                  onNumberBlur={handleBlur}
                />
              </div>
              <div>
                <ATMMultiSelect
                  name={`allowedPaymentGateway`}
                  required
                  showAddButton
                  // placeholder="select allowedPaymentGateway"
                  label="Allowed Payment Gateway"
                  options={paymentGatewayOptions}
                  onChange={(newValue: any) => {
                    setFieldValue(`allowedPaymentGateway`, newValue);
                  }}
                  value={values.allowedPaymentGateway}
                />
              </div>
            </div>

            <FormSectionHeading>Courier Charges</FormSectionHeading>
            <div className="grid grid-cols-12 gap-4 my-3">
              <FieldArray
                name="courierCharges"
                render={({ push, remove }) => (
                  <>
                    {values?.courierCharges?.map((courier: any, index: any) => {
                      return (
                        <div
                          className={`xl:col-span-4 md:col-span-6 xs:col-span-12 gap-4 shadow border  rounded `}
                          key={index}
                        >
                          <div className="flex justify-between items-center px-4 py-2">
                            <div className="text-primary-main">
                              Courier Charges #{index + 1}
                            </div>

                            <button
                              type="button"
                              onClick={() => remove(index)}
                              className="text-red-400 "
                            >
                              <MdDeleteOutline size={24} />
                            </button>
                          </div>
                          <div className="flex flex-col gap-2 p-4">
                            <ATMTextField
                              name={`courierCharges[${index}].day`}
                              label={`Day`}
                              value={courier?.day || ""}
                              onBlur={handleBlur}
                              onChange={(e) => {
                                handleValidNumber(e) &&
                                  setFieldValue(
                                    `courierCharges[${index}].day`,
                                    e.target.value
                                  );
                              }}
                            />
                            <ATMTextField
                              name={`courierCharges[${index}].charge`}
                              label={`Charge`}
                              value={courier?.charge || ""}
                              onBlur={handleBlur}
                              onChange={(e) => {
                                handleValidNumber(e) &&
                                  setFieldValue(
                                    `courierCharges[${index}].charge`,
                                    e.target?.value
                                  );
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                    <div
                      onClick={() =>
                        push({
                          day: "",
                          charge: "",
                        })
                      }
                      className="xl:col-span-4 md:col-span-6 xs:col-span-12 gap-4 border-2 border-dashed border-slate-300 rounded flex flex-col justify-center cursor-pointer min-h-[200px]"
                    >
                      <div className="font-medium text-base flex justify-center items-center">
                        <AiOutlinePlus
                          size={18}
                          color="#0064C4"
                          className="mr-2"
                        />
                      </div>
                      <div className="text-center font-medium">
                        Add Courier Charges
                      </div>
                    </div>
                  </>
                )}
              />
            </div>
            <div>
              <ATMTextArea
                name="impDescription"
                value={values.impDescription}
                onChange={(e) => {
                  setFieldValue("impDescription", e);
                }}
                label="Description"
              />
            </div>
          </>
        )}
        <h1
          onClick={() => toggle("other")}
          className="w-full cursor-pointer flex justify-between items-center p-3 bg-gray-100"
        >
          Other Details
        </h1>
        {open.other && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 p-2">
              <div>
                <ATMSelect
                  name="demoLinkIsAvailable"
                  options={selectOptions}
                  value={values.demoLinkIsAvailable}
                  required
                  onChange={(e) => {
                    setFieldValue("demoLinkIsAvailable", e);
                  }}
                  label="Demo Link Is Available"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMSelect
                  name="isHidden"
                  options={selectOptions}
                  value={values.isHidden}
                  required
                  onChange={(e) => {
                    setFieldValue("isHidden", e);
                  }}
                  label="Is Hidden"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder=" Search key code"
                  name="searchKeyCode"
                  value={values.searchKeyCode}
                  required
                  onChange={(e) => {
                    setFieldValue("searchKeyCode", e.target.value);
                  }}
                  label="Search Key Code"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder=" Attempts"
                  name="attempts"
                  value={values.attempts}
                  required
                  onChange={(e) => {
                    setFieldValue("attempts", e.target.value);
                  }}
                  label="Attempts"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder=" Batch Recording"
                  name="batchRecording"
                  value={values.batchRecording}
                  required
                  onChange={(e) => {
                    setFieldValue("batchRecording", e.target.value);
                  }}
                  label="Batch Recording"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder=" Video Runs On"
                  name="videoRunsOn"
                  value={values.videoRunsOn}
                  required
                  onChange={(e) => {
                    setFieldValue("videoRunsOn", e.target.value);
                  }}
                  label="Video Runs On"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder=" Kit Content"
                  name="kitContent"
                  value={values.kitContent}
                  required
                  onChange={(e) => {
                    setFieldValue("kitContent", e.target.value);
                  }}
                  label="Kit Content"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder=" Lecture Recording Date"
                  name="lectureRecordingDate"
                  value={values.lectureRecordingDate}
                  required
                  onChange={(e) => {
                    setFieldValue("lectureRecordingDate", e.target.value);
                  }}
                  label="Lecture Recording Date"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder="Language"
                  name="language"
                  value={values.language}
                  required
                  onChange={(e) => {
                    setFieldValue("language", e.target.value);
                  }}
                  label="Language"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder="No Of Lectures"
                  name="noOfLectures"
                  value={values.noOfLectures}
                  required
                  onChange={(e) => {
                    setFieldValue("noOfLectures", e.target.value);
                  }}
                  label="No Of Lectures"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder="Duration"
                  name="duration"
                  value={values.duration}
                  required
                  onChange={(e) => {
                    setFieldValue("duration", e.target.value);
                  }}
                  label="Duration"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder="Study Material"
                  name="studyMaterial"
                  value={values.studyMaterial}
                  required
                  onChange={(e) => {
                    setFieldValue("studyMaterial", e.target.value);
                  }}
                  label="Study Material"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder="No Of Views"
                  name="noOfViews"
                  value={values.noOfViews}
                  required
                  onChange={(e) => {
                    setFieldValue("noOfViews", e.target.value);
                  }}
                  label="No Of Views"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder="View Count As"
                  name="viewCountAs"
                  value={values.viewCountAs}
                  required
                  onChange={(e) => {
                    setFieldValue("viewCountAs", e.target.value);
                  }}
                  label="View Count As"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder="View Extension"
                  name="viewExtension"
                  value={values.viewExtension}
                  required
                  onChange={(e) => {
                    setFieldValue("viewExtension", e.target.value);
                  }}
                  label="View Extension"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder="Validity"
                  name="validity"
                  value={values.validity}
                  required
                  onChange={(e) => {
                    setFieldValue("validity", e.target.value);
                  }}
                  label="Validity"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder="Validity Starts From"
                  name="validityStartsFrom"
                  value={values.validityStartsFrom}
                  required
                  onChange={(e) => {
                    setFieldValue("validityStartsFrom", e.target.value);
                  }}
                  label="Validity Start From"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder=" Validity Extension"
                  name="validityExtension"
                  value={values.validityExtension}
                  required
                  onChange={(e) => {
                    setFieldValue("validityExtension", e.target.value);
                  }}
                  label="Validity Extension"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder=" Topics Covered"
                  name="topicsCovered"
                  value={values.topicsCovered}
                  required
                  onChange={(e) => {
                    setFieldValue("topicsCovered", e.target.value);
                  }}
                  label="Topics Covered"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder=" Multiple Demo"
                  name="multipleDemo"
                  value={values.multipleDemo}
                  required
                  onChange={(e) => {
                    setFieldValue("multipleDemo", e.target.value);
                  }}
                  label="Multiple Demo"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder=" Processing Time"
                  name="processingTime"
                  value={values.processingTime}
                  required
                  onChange={(e) => {
                    setFieldValue("processingTime", e.target.value);
                  }}
                  label="Processing Time"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder=" Additional Offer"
                  name="additionalOffer"
                  value={values.additionalOffer}
                  required
                  onChange={(e) => {
                    setFieldValue("additionalOffer", e.target.value);
                  }}
                  label="Additional Offer"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder="GD Link"
                  name="gdLink"
                  value={values.gdLink}
                  required
                  onChange={(e) => {
                    setFieldValue("gdLink", e.target.value);
                  }}
                  label=" GD Link"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder="demo link"
                  name="demoLinks"
                  value={values.demoLinks}
                  required
                  onChange={(e) => {
                    setFieldValue("demoLinks", e.target.value);
                  }}
                  label=" Demo Link"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder="demo name"
                  name="demoName"
                  value={values.demoName}
                  required
                  onChange={(e) => {
                    setFieldValue("demoName", e.target.value);
                  }}
                  label=" Demo Name"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder="view demo"
                  name="viewDemo"
                  value={values.viewDemo}
                  required
                  onChange={(e) => {
                    setFieldValue("viewDemo", e.target.value);
                  }}
                  label="View Demo"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder="contact technical support"
                  name="contactTechnicalSupport"
                  value={values.contactTechnicalSupport}
                  required
                  onChange={(e) => {
                    handleValidNumber2(e) &&
                      setFieldValue("contactTechnicalSupport", e.target.value);
                  }}
                  label="Contact Technical Support"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder="contact tracking support"
                  name="contactTrackingSupport"
                  value={values.contactTrackingSupport}
                  required
                  onChange={(e) => {
                    handleValidNumber2(e) &&
                      setFieldValue("contactTrackingSupport", e.target.value);
                  }}
                  label="Contact Tracking Support"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder="additional requirement"
                  name="additionalRequirement"
                  value={values.additionalRequirement}
                  required
                  onChange={(e) => {
                    setFieldValue("additionalRequirement", e.target.value);
                  }}
                  label="Additional Requirement"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder="details amendments"
                  name="detailsAmendments"
                  value={values.detailsAmendments}
                  required
                  onChange={(e) => {
                    setFieldValue("detailsAmendments", e.target.value);
                  }}
                  label="Details Amendments"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder="why to buy"
                  name="whyToBuy"
                  value={values.whyToBuy}
                  required
                  onChange={(e) => {
                    setFieldValue("whyToBuy", e.target.value);
                  }}
                  label="Why To Buy"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder="contact for doubt solving"
                  name="contactForDoubtSolving"
                  value={values.contactForDoubtSolving}
                  required
                  onChange={(e) => {
                    handleValidNumber2(e) &&
                      setFieldValue("contactForDoubtSolving", e.target.value);
                  }}
                  label="Contact For Doubt Solving"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder="shipping method"
                  name="shippingMethod"
                  value={values.shippingMethod}
                  required
                  onChange={(e) => {
                    setFieldValue("shippingMethod", e.target.value);
                  }}
                  label="Shipping Method"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder="meta"
                  name="meta"
                  value={values.meta}
                  required
                  onChange={(e) => {
                    setFieldValue("meta", e.target.value);
                  }}
                  label="Meta"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder="meta title"
                  name="metaTitle"
                  value={values.metaTitle}
                  required
                  onChange={(e) => {
                    setFieldValue("metaTitle", e.target.value);
                  }}
                  label="Meta Title"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder="image alt text"
                  name="imageAltText"
                  value={values.imageAltText}
                  required
                  onChange={(e) => {
                    setFieldValue("imageAltText", e.target.value);
                  }}
                  label="Image Alt Text"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder="rating"
                  name="rating"
                  value={values.rating}
                  required
                  onChange={(e) => {
                    handleValidDecimalNumber(e) &&
                      setFieldValue("rating", e.target.value);
                  }}
                  label="Rating"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder=""
                  name="documentToBeProvidedForOrderProcessing"
                  value={values.documentToBeProvidedForOrderProcessing}
                  required
                  onChange={(e) => {
                    setFieldValue(
                      "documentToBeProvidedForOrderProcessing",
                      e.target.value
                    );
                  }}
                  label="Document To Be Provided For Order Processing"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder=""
                  name="orderGoogleSheetLink"
                  value={values.orderGoogleSheetLink}
                  required
                  onChange={(e) => {
                    setFieldValue("orderGoogleSheetLink", e.target.value);
                  }}
                  label="Order Google Sheet Link"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder=""
                  name="validityExplanationVidUrl"
                  value={values.validityExplanationVidUrl}
                  required
                  onChange={(e) => {
                    setFieldValue("validityExplanationVidUrl", e.target.value);
                  }}
                  label="Validity Explanation Video Url"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder=""
                  name="productExplanationVideoUrl"
                  value={values.productExplanationVideoUrl}
                  required
                  onChange={(e) => {
                    setFieldValue("productExplanationVideoUrl", e.target.value);
                  }}
                  label="Product Explanation Video Url"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder=""
                  name="viewExplanationVidUrl"
                  value={values.viewExplanationVidUrl}
                  required
                  onChange={(e) => {
                    setFieldValue("viewExplanationVidUrl", e.target.value);
                  }}
                  label="View Explanation Video Url"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder=""
                  name="index"
                  value={values.index}
                  required
                  onChange={(e) => {
                    setFieldValue("index", e.target.value);
                  }}
                  label="Index"
                  onBlur={handleBlur}
                />
              </div>
              <div>
                <ATMTextField
                  placeholder=""
                  name="tinyUrl"
                  value={values.tinyUrl}
                  required
                  onChange={(e) => {
                    setFieldValue("tinyUrl", e.target.value);
                  }}
                  label="Tiny Url"
                  onBlur={handleBlur}
                />
              </div>
              <div className="col-span-2">
                <ATMMultiSelect
                  isLoading={isRelevantProductLoading}
                  name={`relevantProducts`}
                  required
                  showAddButton
                  placeholder=""
                  label="Relevant Products"
                  options={getRelevantProductsOptions}
                  onChange={(newValue: any) => {
                    setFieldValue(`relevantProducts`, newValue);
                  }}
                  value={values.relevantProducts}
                />
              </div>
              <div className="col-span-2">
                <ATMMultiSelect
                  isLoading={isRelevantProductLoading}
                  name={`studentAlsoPurchased`}
                  required
                  showAddButton
                  placeholder=""
                  label="Student Also Purchased"
                  options={getRelevantProductsOptions}
                  onChange={(newValue: any) => {
                    setFieldValue(`studentAlsoPurchased`, newValue);
                  }}
                  value={values.studentAlsoPurchased}
                />
              </div>
            </div>

            <div>
              <ATMTextArea
                placeholder=""
                name="additionalDescription"
                value={values.additionalDescription}
                required
                onChange={(e) => {
                  setFieldValue("additionalDescription", e);
                }}
                label="Additional Description"
              />
            </div>

            <div className="my-6">
              <p className="border-l-2 px-2 mt-3 border-black font-medium">
                Unboxing Video Links
              </p>
              {/* Label Name */}
              <FieldArray name="unboxingVidLinks">
                {({ push, remove }) => (
                  <div className="grid grid-cols-4 gap-3 ">
                    {values?.unboxingVidLinks?.map((video: any, index: any) => (
                      <div key={index} className="flex gap-4 items-end">
                        <div className="">
                          <ATMTextField
                            name={`unboxingVidLinks[${index}]`}
                            value={values.unboxingVidLinks[index]}
                            onChange={(e) =>
                              setFieldValue(
                                `unboxingVidLinks[${index}]`,
                                e.target.value
                              )
                            }
                            label="Unboxing Vid Links"
                            placeholder="Enter Video url"
                            onBlur={handleBlur}
                          />
                        </div>

                        {/* Delete Button */}
                        <button
                          type="button"
                          className={`${getInputHeight(
                            "small"
                          )} aspect-square rounded bg-red-400 text-white flex justify-center items-center `}
                          onClick={() => remove(index)}
                        >
                          <RiDeleteBin5Line size={20} />
                        </button>
                      </div>
                    ))}

                    {/* Add More Button */}
                    <button
                      type="button"
                      onClick={() => push("")}
                      className="rounded border-dashed mt-2 border border-slate-500 py-2 font-medium text-md text-gray-400 flex justify-center items-center gap-[2px]"
                    >
                      Add
                      {values?.unboxingVidLinks?.length
                        ? "More"
                        : "Unboxing Vid Links"}
                      <span>
                        <GrFormAdd
                          color="#ADB8C8"
                          size="1.5em"
                          className="font-semibold"
                        />
                      </span>
                    </button>
                  </div>
                )}
              </FieldArray>
            </div>
            <div className="my-6">
              <p className="border-l-2 px-2 mt-3 border-black font-medium">
                image
              </p>
              {/* Label Name */}
              <FieldArray name="image">
                {({ push, remove }) => (
                  <div className="grid grid-cols-4 gap-3 ">
                    {values?.image?.map((img: any, index: any) => (
                      <div key={index} className="flex gap-4 items-end">
                        <div className="w-full">
                          <ATMFilePickerWrapper
                            name={`image[${index}]`}
                            label="Image"
                            accept="image/*"
                            onSelect={(value) =>
                              setFieldValue(`image[${index}]`, value)
                            }
                            selectedFile={values.image[index]}
                          />
                        </div>

                        {/* Delete Button */}
                        <button
                          type="button"
                          className={`${getInputHeight(
                            "small"
                          )} aspect-square rounded bg-red-400 text-white flex justify-center items-center `}
                          onClick={() => remove(index)}
                        >
                          <RiDeleteBin5Line size={20} />
                        </button>
                      </div>
                    ))}

                    {/* Add More Button */}
                    <button
                      type="button"
                      onClick={() => push("")}
                      className="rounded border-dashed mt-2 border border-slate-500 py-2 font-medium text-md text-gray-400 flex justify-center items-center gap-[2px]"
                    >
                      Add {values?.image?.length ? "More" : "Image"}
                      <span>
                        <GrFormAdd
                          color="#ADB8C8"
                          size="1.5em"
                          className="font-semibold"
                        />
                      </span>
                    </button>
                  </div>
                )}
              </FieldArray>
            </div>

            <div>
              <ATMTextArea
                name="remark"
                value={values.remark}
                onChange={(e) => {
                  setFieldValue("remark", e);
                }}
                label="Remark"
              />
            </div>
          </>
        )}
      </div>
      <div className="self-end">
        <ATMLoadingButton
          className="mt-1 w-24"
          isLoading={isSubmitting}
          type="submit"
        >
          Save
        </ATMLoadingButton>
      </div>
    </div>
  );
};

export default ProductManagement;
